import { useTranslation } from 'react-i18next';
import { matchPath, useSearchParams } from 'react-router-dom';

import { useNavigateToAssetOverview } from '~/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { getKnowledgeGraphNavigateParams } from '~/modules/overview-knowledge-graph/utils/knowledge-graph-utils';
import { appPaths, queryKeys } from '~/routes/app-paths';
import { KnowledgeGraphSectionId } from '~/widgets/knowledge-graph-widget/KnowledgeGraphWidget';

import { useTerminalNavigate } from '../use-terminal-navigate/useTerminalNavigate';

export const useKnowledgeGraphDropdownItems = () => {
  const { t } = useTranslation();
  const navigateTerminal = useTerminalNavigate();
  const { navigateToPage } = useNavigateToAssetOverview();

  const [searchParams] = useSearchParams();

  const viewEvidenceItem = ({
    sectionId,
    entityTag,
    separated,
  }: {
    sectionId: KnowledgeGraphSectionId;
    entityTag: string;
    separated?: boolean;
  }) => {
    return {
      label: t('widget:assetOverview.viewEvidence'),
      onClick: () =>
        navigateToPage(
          getKnowledgeGraphNavigateParams({
            sectionId,
            searchParams,
            entityTag,
          })
        ),
      separated,
    };
  };

  const goToThemeOverviewItem = (themeId: string, separated?: boolean) => {
    const newSearchParams = new URLSearchParams();
    if (!location.pathname.includes(appPaths.themeOverview)) {
      newSearchParams.set(queryKeys.history, location.pathname);
    }

    const goTo = `${appPaths.themeOverview}/${themeId}`;
    const isSameThemeOverview = matchPath(
      {
        path: goTo,
        end: false,
        caseSensitive: false,
      },
      location.pathname
    );

    return {
      label: t('widget:themeOverview.goToThemeOverview'),
      onClick: () => {
        if (isSameThemeOverview) {
          return;
        }

        return navigateTerminal({
          to: {
            pathname: goTo,
            search: newSearchParams.toString(),
          },
          crumb: { label: '', themeId },
        });
      },
      separated,
    };
  };

  const goToAssetOverviewItem = () => {
    return {
      label: t('widget:assetOverview.assetOverview'),
      onClick: (selectedEntityTag: string) => {
        navigateTerminal({
          navigateFn: () => navigateToPage({ entityTag: selectedEntityTag }),
          crumb: { label: '', assetTag: selectedEntityTag },
        });
      },
    };
  };

  return {
    viewEvidenceItem,
    goToThemeOverviewItem,
    goToAssetOverviewItem,
  };
};
